import { useState, useMemo, useEffect } from "react";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import type { ChainMetaOutput, TokenMetaOutput, WalletBalance } from "~/api/types";
import { useChains } from "~/hooks/useChains";
import { useTokenList } from "~/hooks/useTokenList";
import { useWalletBalances } from "~/hooks/useWalletBalances";
import { api } from "~/api/client";
import { formatCompactUsd } from "~/lib/format";
import { Dialog, DialogTitle, DialogClose } from "~/components/shared/Dialog";
import { TokenRow } from "./TokenRow";
import { Skeleton } from "~/components/shared/Skeleton";

export function TokenSelector({
  chainId,
  open,
  onOpenChange,
  onSelect,
  onChainSelect,
}: {
  chainId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (token: TokenMetaOutput, chain: ChainMetaOutput) => void;
  onChainSelect?: (chain: ChainMetaOutput) => void;
}) {
  const queryClient = useQueryClient();
  const { chains } = useChains();
  const { balances, balanceMap, chainBalanceMap, totalValueUsd } = useWalletBalances();
  const defaultChainId = chainBalanceMap.size > 0 ? "all" : (chainId ?? "all");
  const [selectedChainId, setSelectedChainId] = useState(defaultChainId);
  const isAllChains = selectedChainId === "all";
  const { tokens, isLoading } = useTokenList(isAllChains ? undefined : selectedChainId);
  const [search, setSearch] = useState("");
  const [chainSearch, setChainSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedChainId(defaultChainId);
    setSearch("");
    setChainSearch("");
  }, [open, defaultChainId]);

  // Prefetch token lists for all chains
  useEffect(() => {
    for (const c of chains) {
      queryClient.prefetchQuery({
        queryKey: ["tokens", c.chainId],
        queryFn: () => api.getTokens(c.chainId),
        staleTime: 60 * 60 * 1000,
      });
    }
  }, [chains, queryClient]);

  const selectedChain = chains.find((c) => c.chainId === selectedChainId);

  // Single-chain filtering (used when a specific chain is selected)
  const filteredTokens = useMemo(() => {
    if (isAllChains || !search) return tokens;
    const q = search.toLowerCase();
    return tokens.filter(
      (t) =>
        t.symbol?.toLowerCase().includes(q) ||
        t.name?.toLowerCase().includes(q) ||
        t.address?.toLowerCase() === q,
    );
  }, [tokens, search, isAllChains]);

  // Single-chain: balance tokens first (may not be in curated list), then curated tokens
  const singleChainTokens = useMemo(() => {
    if (isAllChains) return [];
    const q = search.trim().toLowerCase();
    const seen = new Set<string>();
    const result: Array<{ token: TokenMetaOutput; balance?: WalletBalance }> = [];

    // Balance tokens for this chain first (always at top, may not be in curated list)
    for (const b of balances) {
      if (b.chainId !== selectedChainId) continue;
      if (q && !(
        b.symbol?.toLowerCase().includes(q) ||
        b.name?.toLowerCase().includes(q) ||
        b.address?.toLowerCase() === q
      )) continue;
      const key = b.address.toLowerCase();
      seen.add(key);
      result.push({
        token: { chainId: b.chainId, address: b.address, symbol: b.symbol, name: b.name, decimals: b.decimals, logoUri: b.logoUri },
        balance: balanceMap.get(`${selectedChainId}:${key}`),
      });
    }

    // Then the curated token list (skip duplicates already shown as balance tokens)
    for (const t of filteredTokens) {
      const key = t.address.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ token: t, balance: balanceMap.get(`${selectedChainId}:${key}`) });
    }

    return result;
  }, [isAllChains, filteredTokens, balances, balanceMap, selectedChainId, search]);

  // Reactive subscription to all chains' token lists (for cross-chain search)
  // combine gives us a stable reference so the downstream memo doesn't re-run every render
  const allTokenData = useQueries({
    queries: isAllChains && search.length >= 2
      ? chains.map((c) => ({
          queryKey: ["tokens", c.chainId],
          queryFn: () => api.getTokens(c.chainId),
          staleTime: 60 * 60 * 1000,
        }))
      : [],
    combine: (results) => ({
      tokensByChain: results.map((r) => r.data),
      isLoading: results.some((r) => r.isLoading),
    }),
  });

  // "All chains" token list: balances + cross-chain search results
  const allChainsTokens = useMemo(() => {
    if (!isAllChains) return [];

    const q = search.trim().toLowerCase();
    const seen = new Set<string>();
    const results: Array<{ token: TokenMetaOutput; chain: ChainMetaOutput; balance?: WalletBalance }> = [];

    // Balance tokens first (always shown, filtered by search if present)
    for (const b of balances) {
      if (q && !(
        b.symbol?.toLowerCase().includes(q) ||
        b.name?.toLowerCase().includes(q) ||
        b.address?.toLowerCase() === q
      )) continue;

      const chain = chains.find((c) => c.chainId === b.chainId);
      if (!chain) continue;

      const key = `${b.chainId}:${b.address.toLowerCase()}`;
      seen.add(key);
      results.push({
        token: { chainId: b.chainId, address: b.address, symbol: b.symbol, name: b.name, decimals: b.decimals, logoUri: b.logoUri },
        chain,
        balance: balanceMap.get(key),
      });
    }

    // Cross-chain search: add matching tokens from all cached chains (2+ chars)
    if (q.length >= 2) {
      for (let i = 0; i < allTokenData.tokensByChain.length; i++) {
        const data = allTokenData.tokensByChain[i];
        if (!data?.tokens) continue;
        const chain = chains[i];
        if (!chain) continue;

        for (const t of data.tokens) {
          if (results.length >= 50) break;
          const key = `${t.chainId}:${t.address.toLowerCase()}`;
          if (seen.has(key)) continue;
          seen.add(key);

          if (
            t.symbol?.toLowerCase().includes(q) ||
            t.name?.toLowerCase().includes(q) ||
            t.address?.toLowerCase() === q
          ) {
            results.push({ token: t, chain, balance: balanceMap.get(key) });
          }
        }
        if (results.length >= 50) break;
      }
    }

    return results;
  }, [isAllChains, search, balances, chains, balanceMap, allTokenData.tokensByChain]);

  // Sort chains: chains with balances first (by USD desc), then the rest
  const sortedChains = useMemo(() => {
    const filtered = chainSearch
      ? chains.filter((c) => c.name.toLowerCase().includes(chainSearch.toLowerCase()))
      : chains;
    return [...filtered].sort((a, b) => {
      const balA = chainBalanceMap.get(a.chainId) ?? 0;
      const balB = chainBalanceMap.get(b.chainId) ?? 0;
      if (balA && !balB) return -1;
      if (!balA && balB) return 1;
      if (balA && balB) return balB - balA;
      return 0;
    });
  }, [chains, chainSearch, chainBalanceMap]);

  function handleChainClick(cid: string, chain?: ChainMetaOutput) {
    setSelectedChainId(cid);
    setSearch("");
    if (chain) onChainSelect?.(chain);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <DialogTitle>Select Token</DialogTitle>
      <DialogClose />

      <div className="-mx-6 -mb-6 flex" style={{ height: "min(70vh, 520px)" }}>
        {/* Chain sidebar */}
        <div className="flex w-[56px] shrink-0 flex-col border-r border-[rgba(255,255,255,0.08)] lg:w-[170px]">
          {/* Chain search (desktop only) */}
          <div className="hidden px-2 pt-2 pb-1 lg:block">
            <div className="flex h-9 items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border-modal)] bg-[var(--color-bg-modal-input)] px-2">
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                className="shrink-0 text-[#9b9b9b]"
              >
                <path
                  d="M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10ZM14 14l-3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search chains"
                value={chainSearch}
                onChange={(e) => setChainSearch(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-xs text-[var(--color-text-primary)] placeholder:text-[#9b9b9b] focus:outline-none"
              />
            </div>
          </div>

          <div className="chain-scroll flex-1 overflow-y-auto px-1 pb-2 pt-1" style={{ scrollbarWidth: "thin" }}>
            {/* All chains button */}
            <button
              onClick={() => handleChainClick("all")}
              className={`flex w-full items-center justify-center gap-2.5 rounded-[var(--radius-sm)] px-1.5 py-2 text-left text-[13px] font-medium transition-colors duration-150 lg:justify-start lg:px-2.5 ${
                isAllChains
                  ? "bg-[rgba(59,130,246,0.15)] text-white"
                  : "text-[var(--color-text-secondary)] hover:bg-[rgba(255,255,255,0.04)]"
              }`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="h-6 w-6 shrink-0">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
                <ellipse cx="12" cy="12" rx="4" ry="9" stroke="currentColor" strokeWidth="1.5" />
                <path d="M3.5 9h17M3.5 15h17" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              <div className="hidden min-w-0 flex-col lg:flex">
                <span className="truncate">All chains</span>
                {totalValueUsd > 0 && (
                  <span className="flex items-center gap-0.5 text-[10px] text-[var(--color-text-tertiary)]">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="shrink-0">
                      <path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
                    </svg>
                    {formatCompactUsd(totalValueUsd)}
                  </span>
                )}
              </div>
            </button>

            {sortedChains.map((c) => {
              const active = c.chainId === selectedChainId;
              const chainBal = chainBalanceMap.get(c.chainId);
              return (
                <button
                  key={c.chainId}
                  onClick={() => handleChainClick(c.chainId, c)}
                  className={`flex w-full items-center justify-center gap-2.5 rounded-[var(--radius-sm)] px-1.5 py-2 text-left text-[13px] font-medium transition-colors duration-150 lg:justify-start lg:px-2.5 ${
                    active
                      ? "bg-[rgba(59,130,246,0.15)] text-white"
                      : "text-[var(--color-text-secondary)] hover:bg-[rgba(255,255,255,0.04)]"
                  }`}
                >
                  <img
                    src={c.logoUri}
                    alt={c.name}
                    className="h-6 w-6 shrink-0 rounded-full"
                  />
                  <div className="hidden min-w-0 flex-col lg:flex">
                    <span className="truncate">{c.name}</span>
                    {chainBal != null && chainBal > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-[var(--color-text-tertiary)]">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="shrink-0">
                          <path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
                        </svg>
                        {formatCompactUsd(chainBal)}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Token list */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Search */}
          <div className="px-4 pt-2 pb-3">
            <div className="flex h-12 items-center gap-2 rounded-[var(--radius-sm)] bg-[var(--color-bg-modal-input)] px-3">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="shrink-0 text-[#9b9b9b]"
              >
                <path
                  d="M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10ZM14 14l-3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search for a token or paste address"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                className="min-w-0 flex-1 bg-transparent text-[16px] text-[var(--color-text-primary)] placeholder:text-[#9b9b9b] focus:outline-none"
              />
            </div>
          </div>

          {/* Token rows */}
          <div className="flex-1 overflow-y-auto px-2">
            {isAllChains ? (
              // All chains view
              allChainsTokens.length === 0 ? (
                search.length >= 2 && allTokenData.isLoading ? (
                  <div className="flex flex-col gap-1 px-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-[60px] w-full rounded-[var(--radius-sm)]" />
                    ))}
                  </div>
                ) : (
                  <p className="py-12 text-center text-sm text-[#9ca3af]">
                    {search ? "No tokens found" : "Connect wallet to see your tokens"}
                  </p>
                )
              ) : (
                <div className="flex flex-col">
                  {allChainsTokens.map((r) => (
                    <TokenRow
                      key={`${r.token.chainId}:${r.token.address}`}
                      token={r.token}
                      chain={r.chain}
                      balance={r.balance}
                      onClick={() => {
                        setSelectedChainId(r.chain.chainId);
                        onChainSelect?.(r.chain);
                        onSelect(r.token, r.chain);
                      }}
                    />
                  ))}
                </div>
              )
            ) : (
              // Single chain view
              isLoading ? (
                <div className="flex flex-col gap-1 px-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-[60px] w-full rounded-[var(--radius-sm)]" />
                  ))}
                </div>
              ) : singleChainTokens.length === 0 ? (
                <p className="py-12 text-center text-sm text-[#9ca3af]">
                  No tokens found
                </p>
              ) : (
                <div className="flex flex-col">
                  {singleChainTokens.map((r) => (
                    <TokenRow
                      key={r.token.address}
                      token={r.token}
                      chain={selectedChain}
                      balance={r.balance}
                      onClick={() => {
                        const chain = chains.find((c) => c.chainId === selectedChainId);
                        if (chain) onSelect(r.token, chain);
                      }}
                    />
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
